import type { Component, VNode } from 'vue'

export type ToastType = 'default' | 'success' | 'error' | 'warning' | 'info' | 'loading'

export type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'

export const TOAST_POSITIONS = [
  'top-left',
  'top-center',
  'top-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
] as const satisfies readonly ToastPosition[]

interface ToastAction {
  label: string
  onClick: (event: MouseEvent) => void
}

export interface ToastOptions {
  /** Reuse an id to UPDATE an existing toast in place (powers loading→success). */
  id?: number | string
  /** Auto-dismiss delay in ms. `Infinity` keeps the toast until dismissed. */
  duration?: number
  description?: string
  /** Custom leading icon; overrides the status icon for the toast's `type`. */
  icon?: Component
  action?: ToastAction
  cancel?: ToastAction
  /** Fired whenever the toast is dismissed (manually, by swipe, or on timeout). */
  onDismiss?: (toast: ToastRecord) => void
  /** Fired when the toast auto-closes because its timer elapsed. */
  onAutoClose?: (toast: ToastRecord) => void
  /** Force-show the close (×) button for this toast, overriding the toaster default. */
  closeButton?: boolean
  /** Pin this toast to a specific corner, overriding the toaster's position. */
  position?: ToastPosition
  /** When `false`, the toast can't be swiped or closed (used by loading states). */
  dismissible?: boolean
  type?: ToastType
}

/** The stored shape: `ToastOptions` plus the fields the store guarantees. */
export interface ToastRecord extends ToastOptions {
  id: number | string
  type: ToastType
  title: string
  createdAt: number
  /** Custom renderer - when present the standard card chrome is bypassed. */
  render?: (toast: ToastRecord) => VNode
  /** Set true when dismissal starts, so the exit animation can play first. */
  dismissed?: boolean
}

export interface PromiseData<T = unknown> {
  loading: string
  success: string | ((data: T) => string)
  error: string | ((error: unknown) => string)
  description?: string
  id?: number | string
}
